import { Button } from "@/components/ui/button";
import { CheckCircle2, ExternalLink, Zap, Share2 } from "lucide-react";
import { toast } from "sonner";

interface Props {
  result: {
    lead: { name: string; address: string };
    measurement: { totalSquares: number; source: string };
    pricing: { good: number; better: number; best: number };
    proposal: { shareId: string };
  };
  onView: () => void;
  onNew: () => void;
}

export function StepReady({ result, onView, onNew }: Props) {
  const shareUrl = `${window.location.origin}/proposal/${result.proposal.shareId}`;

  const share = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Roofing Proposal for ${result.lead.name}`,
          text: `Here's your custom roofing quote.`,
          url: shareUrl,
        });
      } catch {/* user cancelled */}
    } else {
      await navigator.clipboard.writeText(shareUrl);
      toast.success("Link copied");
    }
  };

  return (
    <div className="px-5 pt-6 pb-8 space-y-6">
      <div className="text-center space-y-3 pt-4">
        <div className="mx-auto h-16 w-16 rounded-full bg-primary/15 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-primary" />
        </div>
        <h2 className="text-2xl font-bold tracking-tight">Quote ready 🎉</h2>
        <p className="text-sm text-muted-foreground">
          {result.measurement.totalSquares.toFixed(1)} squares measured via {result.measurement.source}
        </p>
      </div>

      {/* Pricing */}
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "Good", val: result.pricing.good },
          { label: "Better", val: result.pricing.better, hi: true },
          { label: "Best", val: result.pricing.best },
        ].map((p) => (
          <div
            key={p.label}
            className={`rounded-2xl border p-3 text-center ${
              p.hi ? "border-primary bg-primary/10" : "border-border bg-card"
            }`}
          >
            <div className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{p.label}</div>
            <div className={`text-base font-bold mt-1 tabular-nums ${p.hi ? "text-primary" : ""}`}>
              ${p.val.toLocaleString()}
            </div>
          </div>
        ))}
      </div>

      {/* Customer */}
      <div className="rounded-2xl border border-border/60 bg-card p-4 space-y-1">
        <div className="text-sm font-semibold">{result.lead.name}</div>
        <div className="text-xs text-muted-foreground">{result.lead.address}</div>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <Button onClick={onView} className="w-full h-12 gap-2 font-semibold" size="lg">
          <ExternalLink className="h-4 w-4" /> Hand to Homeowner
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <Button onClick={share} variant="outline" className="gap-2">
            <Share2 className="h-4 w-4" /> Share Link
          </Button>
          <Button onClick={onNew} variant="outline" className="gap-2">
            <Zap className="h-4 w-4" /> New Quote
          </Button>
        </div>
      </div>
    </div>
  );
}
